import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Chip, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BADGE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

/**
 * The states this chip can render. Kept as a local string union (rather than importing the
 * `EffectiveEmployeeStatus` enum) so the chip stays a pure, dependency-light UIToolkit primitive
 * usable from any feature. The four effective statuses plus `Unmapped` — a display-only "we have
 * no interpretation of this status yet / needs attention" state (e.g. an unmapped raw HRIS status).
 */
export type EmployeeStatusChipStatus = "Active" | "Inactive" | "OnLeave" | "Suspended" | "Unmapped";

type Props = {
	status: EmployeeStatusChipStatus;
	/** Free-text/preset reason behind the status — shown in the tooltip for OnLeave/Suspended. */
	reason?: string | null;
	/** Window end date (exclusive) — shown in the tooltip ("until <date>") when present. */
	endDate?: DateTime | null;
	size?: "small" | "medium";
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * `dashed` marks the "needs attention / not yet set" look (Unmapped): a dashed border + leading
 * help icon read as a placeholder, clearly distinct from the four concrete, solid-bordered statuses.
 */
const statusVisual = (status: EmployeeStatusChipStatus): { paletteKey: "success" | "warning" | "error" | "grey"; dashed: boolean } => {
	switch (status) {
		case "Active":
			return { paletteKey: "success", dashed: false };
		case "OnLeave":
			return { paletteKey: "warning", dashed: false };
		case "Suspended":
			return { paletteKey: "error", dashed: false };
		case "Unmapped":
			return { paletteKey: "warning", dashed: true };
		default:
			return { paletteKey: "grey", dashed: false };
	}
};

const statusLabelKey: Record<EmployeeStatusChipStatus, string> = {
	Active: "employee.status.chip.active",
	Inactive: "employee.status.chip.inactive",
	OnLeave: "employee.status.chip.on_leave",
	Suspended: "employee.status.chip.suspended",
	Unmapped: "employee.status.chip.unmapped",
};

/**
 * Generic, tokenized badge for a nurse's effective per-department employment status.
 *
 * Colors come from the theme status palette (never hardcoded); OnLeave/Suspended get an
 * optional tooltip carrying the reason and window end date.
 */
export const EmployeeStatusChip: FC<Props> = ({ status, reason, endDate, size = "small", sx, dataTestId }) => {
	const { t } = useTranslation();
	const { paletteKey, dashed } = statusVisual(status);
	const label = t(statusLabelKey[status]);

	const tooltipLines: ReactNode[] = [];
	if (reason) {
		tooltipLines.push(t("employee.status.chip.reason", { reason }));
	}
	if (endDate) {
		tooltipLines.push(t("employee.status.chip.until", { date: endDate.toFormat("MMM d, yyyy") }));
	}

	const chip = (
		<Chip
			label={label}
			size={size}
			icon={dashed ? <HelpOutlineIcon /> : undefined}
			data-testid={dataTestId}
			aria-label={[label, reason, endDate?.toFormat("MMM d, yyyy")].filter(Boolean).join(", ")}
			sx={[
				(theme) => {
					const tone = paletteKey === "grey" ? { main: theme.palette.text.secondary, dark: theme.palette.text.primary } : theme.palette[paletteKey];
					return {
						height: size === "small" ? BADGE.STATUS_CHIP_HEIGHT_SM : BADGE.STATUS_CHIP_HEIGHT_MD,
						fontWeight: 600,
						fontSize: size === "small" ? theme.typography.caption.fontSize : theme.typography.body2.fontSize,
						color: tone.dark,
						backgroundColor: alpha(tone.main, 0.14),
						border: `1px ${dashed ? "dashed" : "solid"} ${alpha(tone.main, dashed ? 0.55 : 0.28)}`,
						"& .MuiChip-label": { px: 1 },
						"& .MuiChip-icon": { color: tone.dark, ml: 0.75, mr: -0.25, fontSize: size === "small" ? "0.95rem" : "1.05rem" },
					};
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		/>
	);

	if (tooltipLines.length === 0) {
		return chip;
	}

	return (
		<InHouseTooltip
			title={
				<span>
					{tooltipLines.map((line, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable tooltip lines
						<span key={idx} style={{ display: "block" }}>
							{line}
						</span>
					))}
				</span>
			}
			arrow
		>
			<span style={{ display: "inline-flex" }}>{chip}</span>
		</InHouseTooltip>
	);
};

export default EmployeeStatusChip;
